import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Button, Header, Icon, Loader, Popup, Table } from 'semantic-ui-react';
import { Accounts, Dividends } from 'actions';
import { Segment } from 'components';
import { colors } from 'utils/colors';
import { formatNum } from 'utils/helpers';
import MultiSelect from '../../../components/Multiselect';
import { translation as translationTools } from '../../Translation/tools';

const MONTH_KEY = /^\d{4}-\d{2}$/;
const MAX_CHIPS = 3;

class DividendsTool extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedAccountIds: [],
      displayedAccountIds: [],
      accountError: '',
      noHoldings: false,
      // The account selector is only shown when the user did not arrive with
      // accounts already chosen (i.e. from the overview page)
      fromOverview: !(props.dividends.preSelectedAccounts || []).length,
    };
  }

  componentDidMount() {
    const { dispatch, accountList } = this.props;

    if (accountList) {
      this.initializeData(accountList);
    } else {
      dispatch(Accounts.fetchAll());
    }
  }

  componentDidUpdate(prevProps) {
    const { accountList } = this.props;

    if (accountList && !prevProps.accountList) {
      this.initializeData(accountList);
    }
  }

  getText = () => {
    const { french } = this.props;

    return translationTools[french ? 'fr' : 'en'].DIVIDENDS;
  };

  // Sums quantities by security across the chosen accounts. GICs and cash
  // don't pay dividends, so they're left out of the request.
  getSymbolsAndQuantities = (accountIds) => {
    const { accountList } = this.props;
    const quantities = (accountList || [])
      .filter((account) => accountIds.includes(account.id))
      .reduce((acc, account) => {
        account.holdings.forEach((holding) => {
          const { security, quantity, gic_ticker } = holding;
          if (!security || gic_ticker || security === 'CASH:MKT' || !quantity)
            return;
          acc[security] = (acc[security] || 0) + quantity;
        });
        return acc;
      }, {});

    return {
      symbols: Object.keys(quantities),
      quantities: Object.values(quantities),
    };
  };

  setSelectedAccounts = (selectedAccountIds) =>
    this.setState({ selectedAccountIds, accountError: '' });

  getCurrency = () => {
    const { user_region } = this.props;

    return user_region === 'US' ? 'USD' : 'CAD';
  };

  getMonths = () => {
    const { dividends } = this.props;
    const keys = new Set(
      Object.keys(dividends.total[this.getCurrency()] || {})
    );
    dividends.table.forEach((row) =>
      Object.keys(row).forEach((key) => keys.add(key))
    );

    return [...keys].filter((key) => MONTH_KEY.test(key)).sort();
  };

  getMonthLabel = (monthKey) => {
    const { french } = this.props;
    const [year, month] = monthKey.split('-').map(Number);

    return new Date(year, month - 1, 1).toLocaleDateString(
      french ? 'fr-CA' : 'en-CA',
      { month: 'short', year: 'numeric' }
    );
  };

  getAccountLabels = () => {
    const { accountList } = this.props;
    const { displayedAccountIds } = this.state;
    if (!accountList) return [];

    const aggregationIds = accountList
      .filter((account) => account.for_aggregation)
      .map((account) => account.id);
    const isAllAccounts =
      aggregationIds.length === displayedAccountIds.length &&
      aggregationIds.every((id) => displayedAccountIds.includes(id));
    if (isAllAccounts) return null;

    return accountList
      .filter((account) => displayedAccountIds.includes(account.id))
      .map((account) => this.getAccountName(account));
  };

  getAccountName = (account) =>
    [account.user_institution, account.label].filter(Boolean).join(': ');

  initializeData = (accountList) => {
    const { dividends } = this.props;
    const preSelectedAccountIds = dividends.preSelectedAccounts || [];

    // Reuse the previous result if we have one and no new accounts were chosen
    if (!preSelectedAccountIds.length && dividends.accounts.length) {
      return this.setState({
        selectedAccountIds: dividends.accounts,
        displayedAccountIds: dividends.accounts,
      });
    }

    const accountIds = preSelectedAccountIds.length
      ? preSelectedAccountIds
      : accountList
          .filter((account) => account.for_aggregation)
          .map((account) => account.id);

    this.setState({ selectedAccountIds: accountIds });
    return this.fetchDividends(accountIds);
  };

  fetchDividends = (accountIds) => {
    const { dispatch } = this.props;

    if (!accountIds.length) {
      return this.setState({
        accountError: this.getText().SELECT_ACCOUNT_ERROR,
      });
    }

    const { symbols, quantities } = this.getSymbolsAndQuantities(accountIds);
    if (!symbols.length) {
      return this.setState({
        noHoldings: true,
        accountError: '',
        displayedAccountIds: accountIds,
      });
    }

    this.setState({ noHoldings: false, accountError: '' });
    return dispatch(Dividends.fetch(symbols, quantities, accountIds)).then(() =>
      this.setState({ displayedAccountIds: accountIds })
    );
  };

  formatAmount = (value) =>
    value === null || value === undefined ? '-' : formatNum(value, '$');

  renderAccountChips = () => {
    const t = this.getText();
    const labels = this.getAccountLabels();

    return (
      <AccountChipsRow>
        <span className="showing-label">
          <Icon name="book" style={{ color: 'black', marginRight: 0 }} />
          {t.SHOWING}
        </span>
        {labels === null ? (
          <span className="chip">{t.ALL_ACCOUNTS}</span>
        ) : (
          <React.Fragment>
            {labels
              .slice(0, labels.length > MAX_CHIPS + 1 ? MAX_CHIPS : undefined)
              .map((label) => (
                <span key={label} className="chip">
                  {label}
                </span>
              ))}
            {labels.length > MAX_CHIPS + 1 && (
              <span className="chip-overflow">
                {`+${labels.length - MAX_CHIPS} ${t.MORE}`}
              </span>
            )}
          </React.Fragment>
        )}
      </AccountChipsRow>
    );
  };

  renderAccountSelector = () => {
    const { accountList } = this.props;
    const { selectedAccountIds, accountError } = this.state;
    const t = this.getText();
    const items = (accountList || []).map((account) => ({
      id: account.id,
      name: this.getAccountName(account),
    }));

    return (
      <div className="accounts">
        <div className="accounts-select">
          <MultiSelect
            items={items}
            selectedItems={selectedAccountIds}
            setSelectedItems={this.setSelectedAccounts}
            placeholder={t.SELECT_ACCOUNTS}
            maxHeight="300px"
          />
          <Popup
            trigger={
              <Button
                icon
                onClick={() => this.fetchDividends(selectedAccountIds)}
                style={{ background: colors.periwinkle, color: 'white' }}
              >
                <Icon name="refresh" />
              </Button>
            }
            content={t.RECALCULATE}
            position="top center"
          />
        </div>
        {accountError && (
          <ErrorText>
            <Icon name="warning sign" />
            {accountError}
          </ErrorText>
        )}
      </div>
    );
  };

  renderStats = () => {
    const { dividends } = this.props;
    const t = this.getText();
    const currency = this.getCurrency();
    const { dividendYield } = dividends;

    return (
      <StatsRow>
        <StatCard>
          <div className="label">{t.PORTFOLIO_VALUE}</div>
          <div className="value">
            {this.formatAmount(dividends.portfolioValue[currency])}
            <span className="currency">{currency}</span>
          </div>
        </StatCard>
        <StatCard>
          <div className="label">{t.TOTAL_ANNUAL_DIVIDENDS}</div>
          <div className="value">
            {this.formatAmount(dividends.annualDividend[currency])}
            <span className="currency">{currency}</span>
          </div>
        </StatCard>
        <StatCard>
          <div className="label">{t.DIVIDEND_YIELD}</div>
          <div className="value">
            {dividendYield === null ? '-' : formatNum(dividendYield * 100, '%')}
          </div>
        </StatCard>
      </StatsRow>
    );
  };

  renderTable = () => {
    const { dividends } = this.props;
    const t = this.getText();
    const currency = this.getCurrency();
    const months = this.getMonths();
    const totals = dividends.total[currency] || {};

    return (
      <TableWrapper>
        <Table unstackable style={{ margin: 0 }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t.HOLDING}</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">
                {t.QUANTITY}
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="center">
                {t.CURRENCY}
              </Table.HeaderCell>
              {months.map((month) => (
                <Table.HeaderCell
                  key={month}
                  textAlign="center"
                  className="month"
                >
                  {this.getMonthLabel(month)}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {dividends.table.map((row) => (
              <Table.Row key={row.ticker}>
                <Table.Cell className="holding">
                  <div className="ticker">{row.ticker}</div>
                  <div className="name">{row.name}</div>
                </Table.Cell>
                <Table.Cell textAlign="center">
                  {Number(row.quantity).toLocaleString()}
                </Table.Cell>
                <Table.Cell textAlign="center">
                  {row.currency || '-'}
                </Table.Cell>
                {months.map((month) => (
                  <Table.Cell key={month} textAlign="center">
                    {this.formatAmount(row[month])}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
          <Table.Footer>
            <Table.Row className="total">
              <Table.HeaderCell>{`${t.TOTAL} (${currency})`}</Table.HeaderCell>
              <Table.HeaderCell />
              <Table.HeaderCell />
              {months.map((month) => (
                <Table.HeaderCell key={month} textAlign="center">
                  {this.formatAmount(totals[month])}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Footer>
        </Table>
      </TableWrapper>
    );
  };

  render() {
    const { dividends, isFetchingAcc, accountList } = this.props;
    const { fromOverview, noHoldings } = this.state;
    const t = this.getText();
    const isFetching = dividends.isFetching || isFetchingAcc;
    const accountsAreEmpty = !!accountList && !accountList.length;
    const hasData = !noHoldings && dividends.table.length > 0;

    return (
      <Segment
        margin="1rem"
        textAlign="center"
        style={{
          position: 'relative',
          minHeight: '700px',
          padding: '2rem 3rem',
        }}
      >
        <Header size="large" textAlign="left" style={{ margin: '0 0 1rem 0' }}>
          {t.TITLE}
        </Header>
        <TopRow>
          <Header size="large" textAlign="left" style={{ margin: 0 }}>
            <Header.Subheader>{t.INTRO}</Header.Subheader>
            {this.renderAccountChips()}
          </Header>
          {fromOverview && this.renderAccountSelector()}
        </TopRow>

        {isFetching ? (
          <Loader active />
        ) : (
          <div>
            {accountsAreEmpty && (
              <WarningMessage>
                <Icon name="warning sign" />
                {t.REQUIRES_ACCOUNTS}
              </WarningMessage>
            )}
            {!accountsAreEmpty && noHoldings && (
              <WarningMessage>{t.NO_HOLDINGS}</WarningMessage>
            )}
            {hasData && (
              <React.Fragment>
                {this.renderStats()}
                {this.renderTable()}
                {dividends.excluded.length > 0 && (
                  <Note>{`${t.EXCLUDED}${dividends.excluded.join(', ')}`}</Note>
                )}
              </React.Fragment>
            )}
          </div>
        )}
      </Segment>
    );
  }
}

DividendsTool.propTypes = {
  dispatch: PropTypes.func.isRequired,
  accountList: PropTypes.array,
  dividends: PropTypes.object.isRequired,
  isFetchingAcc: PropTypes.bool,
  user_region: PropTypes.string,
  french: PropTypes.bool,
};

DividendsTool.defaultProps = {
  accountList: null,
  isFetchingAcc: false,
  user_region: 'CA',
  french: false,
};

export default connect((state) => ({
  accountList: state.Accounts.accountList,
  dividends: state.Dividends,
  isFetchingAcc: state.Accounts.isFetching,
  user_region: state.Storage.user_region || 'CA',
  french: state.Storage.language === 'fr',
}))(DividendsTool);

const TopRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: start;
  column-gap: 2rem;
  text-align: left;
  margin-bottom: 2rem;

  .accounts {
    display: flex;
    flex-direction: column;
    width: 40%;
    align-self: end;

    .accounts-select {
      display: flex;
      flex-direction: row;
      column-gap: 5px;
    }
  }
`;

const StatsRow = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  flex: 1;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  text-align: left;

  .label {
    color: ${colors.darkGrey};
    font-weight: bold;
    margin-bottom: 0.5rem;
  }

  .value {
    color: ${colors.navyBlue};
    font-size: 1.6rem;
    font-weight: bold;
  }

  .currency {
    color: ${colors.darkGrey};
    font-size: 0.9rem;
    margin-left: 0.4rem;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  text-align: left;

  th,
  td {
    white-space: nowrap;
  }

  .month {
    text-transform: capitalize;
  }

  .holding {
    white-space: normal !important;
    min-width: 220px;

    .ticker {
      font-weight: bold;
    }

    .name {
      color: ${colors.darkGrey};
      font-size: 0.9rem;
    }
  }

  .total th {
    font-weight: bold !important;
  }
`;

const WarningMessage = styled.div`
  color: ${colors.red};
  font-weight: bold;
  margin-top: 2rem;
`;

const ErrorText = styled.div`
  color: ${colors.red};
  font-size: 15px;
  margin-top: 5px;
`;

const Note = styled.div`
  color: ${colors.darkGrey};
  text-align: left;
  margin-top: 1rem;
`;

const AccountChipsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 0.5rem;
  font-size: 14px;
  color: ${colors.darkGrey};

  .showing-label {
    display: flex;
    gap: 6px;
  }

  .chip {
    background: #f0f2f5;
    border-radius: 12px;
    padding: 2px 10px;
    color: #333;
    white-space: nowrap;
  }

  .chip-overflow {
    background: #dce4f5;
    color: ${colors.navyBlue};
    border-radius: 12px;
    padding: 2px 10px;
    white-space: nowrap;
  }
`;
